import  { type Channel, type ProgramGuide, OnDemandData, type OnDemandItem, type ContentProperties, type ContentType, ContentItemProps, PlayItem } from "@ca.vmedia.tv/domains";
import { Services } from "@ca.vmedia.tv/services";
import { useDetailPageStore } from "@/stores/DetailPage";
import { useChannelsStore } from "@/stores/Channels";
import { Logger } from '@/utils';
import { AppConstants } from "@/common";
import type { ExposeInterface } from './types';
import { useUserInfoStore } from "@/stores/UserInfo";
import type { AxiosError } from 'axios';
import { useRoute } from 'vue-router';

export const useDetailProgramPageStore = defineStore('DetailProgramPage', (): ExposeInterface => {
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isClient,
    } = storeToRefs(UserInfoStoreModule);

    const { getChannelById } = useChannelsStore();

    const DetailPageStore = useDetailPageStore();
    const {
        getCurrentItemDetail
    } = storeToRefs(DetailPageStore);

    const route = useRoute();

    const _relatedChannels: Ref<Channel[] | null> = ref(null);
    const getRelatedChannels: ComputedRef<Channel[] | null> = computed(() => _relatedChannels.value ?? []);
    const fetchRelatedChannels = async (id: string): Promise<void> => {
        try {
            _relatedChannels.value = await Services.Instance.channels.loadShowtimesChannels(Number(id));
        } catch (error) {
            Logger.captureError(error as Error);
        }
    };

    const _currentChannel: Ref<Channel | null> = ref(null);
    const setCurrentChannel = (channel: Channel | null): void => {
        if(channel != null) {
            _currentChannel.value = channel;
            return;
        }

        const channelId = getCurrentItemDetail.value?.recentAirTime?.channelId;
        if (channelId) {
            _currentChannel.value = getChannelById(channelId)[0];
        } else {
            throw new Error(`Channel ID not found for current item detail: ${route.fullPath}`);
        }
    };
    const getCurrentChannel: ComputedRef<Channel | null> = computed(() => _currentChannel.value);

    const _timerProgramsData: Ref<number | null> = ref(null);
    const getTimerIdPrograms: ComputedRef<number | null> = computed(() => _timerProgramsData.value);

    const _programsProperties: Ref<ContentProperties[]> = ref([]);
    const getProgramsProperties: ComputedRef<ContentProperties[]> = computed(() => _programsProperties.value);

    const _programs: Ref<OnDemandData | null> = ref(null);
    const getPrograms: ComputedRef<OnDemandData | null> = computed(() => _programs.value);
    const fetchPrograms = async (): Promise<void> => {
        try {
            _clearPrograms();
            const programId = getCurrentItemDetail.value?.recentAirTime?.id;
            const channelId = _currentChannel.value ? _currentChannel.value.id : getCurrentItemDetail.value?.recentAirTime?.channelId;

            if (!programId || !channelId) {
                throw new Error('Program id or channel id is not found');
            }
            const data: ProgramGuide = await Services.Instance.guide.loadShowtimes(programId, channelId, 1, AppConstants.ItemsInLine);
            
            _programs.value = new OnDemandData(data.items, null, data.paging);
        } catch (error) {
            Logger.captureError(error as Error);
        }
    };
    const _clearPrograms = (): void => {
        _programs.value = null;
        _programsProperties.value = [];
    };

    const clearAllDetailProgramPage = (): void => {
        _clearPrograms();
        _currentChannel.value = null;
        _relatedChannels.value = null;
        _timerProgramsData.value = null
    };

    watch(
        _currentChannel,
        (value: Channel | null) => {
            if (value !== null) {
                void fetchPrograms();

                if (!_timerProgramsData.value) {
                    _timerProgramsData.value = window.setInterval(
                        () => {
                            void fetchPrograms();
                        },
                        AppConstants.HomeRenewInterval
                    );
                }
            }
        },
    );

    watch(
        () => getPrograms.value ? getPrograms.value.items : null,
        (items) => {
            if(isClient.value && items && getCurrentChannel.value !== null) {
                const ids = items?.filter((itemProgram: OnDemandItem) => PlayItem.isVCloud(itemProgram, getCurrentChannel.value))
                .map((itemProgram: OnDemandItem) => itemProgram.id);
                Services.Instance.domains.mediaRepository.getItemProperties(
                    ids as number[], 
                    getCurrentItemDetail.value?.contentType as ContentType, 
                    [ContentItemProps.WatchedPosition]
                ).then((streamProperties) => {
                    _programsProperties.value = [..._programsProperties.value, ...streamProperties];
                }).catch((error) => {
                    Logger.captureError(error as AxiosError | Error);
                });
            }
        }
    );

    return {
        clearAllDetailProgramPage,
        fetchRelatedChannels,
        setCurrentChannel,
        fetchPrograms,
        getRelatedChannels,
        getCurrentChannel,
        getTimerIdPrograms,
        getPrograms,
        getProgramsProperties
    };
});
